import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Brain, LineChart, Map, Newspaper, Sparkles } from "lucide-react";

import { fetchEventInsights, fetchLeadInsights, fetchVenueInsights, type CRMInsightResponse } from "@/api/insights";
import { useToast } from "@/components/ToastProvider";
import { CRMEpisodesList } from "@/components/memory/CRMEpisodesList";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import { Skeleton } from "@/components/ui/skeleton";

type CRMInsightsPanelProps = {
  objectType: "lead" | "event" | "venue";
  objectId?: string | number | null;
  objectName?: string | null;
};

const fetcherByType = {
  lead: fetchLeadInsights,
  event: fetchEventInsights,
  venue: fetchVenueInsights,
};

const actionButtons: Array<{ id: string; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: "enrich", label: "Queue enrichment workflow", icon: Sparkles },
  { id: "episodes", label: "Open memory browser", icon: Activity },
];

function normalizeScore(data?: Record<string, unknown> | null) {
  if (!data) return null;
  const candidates = ["score", "booking_score", "last_booking_score", "suitability_score"];
  for (const key of candidates) {
    const value = data[key];
    if (typeof value === "number") return value;
    if (typeof value === "string" && !Number.isNaN(Number(value))) return Number(value);
  }
  return null;
}

function formatScore(value?: number | null) {
  if (value === undefined || value === null) return "—";
  return `${Math.round(value * 10) / 10}`;
}

function renderSignalSummary(signal?: { summary?: string | null; timestamp?: string | null } | null) {
  if (!signal?.summary && !signal?.timestamp) return "No activity logged";
  const time = signal?.timestamp ? new Date(signal.timestamp).toLocaleString() : null;
  return `${signal?.summary ?? "Activity recorded"}${time ? ` • ${time}` : ""}`;
}

function extractAudienceMap(insights?: CRMInsightResponse | null) {
  const audience = insights?.tools && typeof insights.tools === "object" ? (insights.tools as Record<string, unknown>).audience_map : null;
  if (!audience || typeof audience !== "object") return null;
  return audience as Record<string, unknown>;
}

export function CRMInsightsPanel({ objectType, objectId, objectName }: CRMInsightsPanelProps) {
  const [insights, setInsights] = useState<CRMInsightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { push } = useToast();

  useEffect(() => {
    if (!objectId) {
      setInsights(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetcher = fetcherByType[objectType];
    fetcher(objectId)
      .then((payload) => {
        if (cancelled) return;
        setInsights(payload ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Unable to load insights.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [objectId, objectType]);

  const score = useMemo(() => {
    if (objectType === "lead") {
      return normalizeScore(insights?.lead);
    }
    if (objectType === "venue") {
      return normalizeScore(insights?.venue);
    }
    return normalizeScore(insights?.event);
  }, [insights?.event, insights?.lead, insights?.venue, objectType]);

  const signals = insights?.episode_signals;
  const lastContact = signals?.last_contact && typeof signals.last_contact === "object" ? (signals.last_contact as { summary?: string | null; timestamp?: string | null }) : null;
  const lastPromo = signals?.last_promo && typeof signals.last_promo === "object" ? (signals.last_promo as { summary?: string | null; timestamp?: string | null }) : null;
  const lastSettlement = signals?.last_settlement && typeof signals.last_settlement === "object" ? (signals.last_settlement as { summary?: string | null; timestamp?: string | null }) : null;
  const lastPress = signals?.last_press_reference && typeof signals.last_press_reference === "object" ? (signals.last_press_reference as { summary?: string | null; timestamp?: string | null }) : null;
  const audienceMap = extractAudienceMap(insights);

  const handleAction = (actionId: string) => {
    if (!objectId) {
      push({ title: "Missing object", message: "Select a record before running actions." });
      return;
    }
    if (actionId === "episodes") {
      navigate(`/memory?tab=episodes&${objectType}_id=${objectId}`);
      return;
    }
    push({
      title: "Action queued",
      message: `${objectName ?? objectId} enrichment request submitted.`,
    });
  };

  return (
    <aside className="rounded-2xl border border-border bg-background/70 p-4 shadow-2xl shadow-black/40">
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200">CRM Intelligence</p>
        <h2 className="text-xl font-semibold text-foreground">Live Agent Insights</h2>
        <p className="text-sm text-muted-foreground">Lead scoring, engagement summaries, and music analytics in one panel.</p>
      </div>
      {loading && (
        <div className="mt-4 space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}
      {!objectId && <p className="mt-3 text-sm text-muted-foreground">Select a {objectType} to view insights.</p>}
      {insights && objectId ? (
        <div className="mt-4 space-y-5">
          {insights.summary && (
            <section className="rounded-xl border border-border/60 bg-background/40 p-3 text-sm text-foreground/80">
              <p className="text-xs uppercase tracking-wide text-amber-300">Lead score explanation</p>
              <p className="mt-1 text-foreground">{insights.summary}</p>
            </section>
          )}

          <section className="grid gap-3 rounded-xl border border-border/60 bg-background/50 p-3 text-xs uppercase tracking-wide text-muted-foreground">
            <div>
              <p>Stage</p>
              <p className="text-base font-semibold text-foreground">
                {(insights.lead?.pipeline_stage as string) ??
                  (insights.lead?.status as string) ??
                  (insights.event?.status as string) ??
                  "Unknown"}
              </p>
            </div>
            <div>
              <p>Engagement</p>
              <p className="text-sm text-foreground/80">{renderSignalSummary(lastContact)}</p>
            </div>
            <div>
              <p>{objectType === "venue" ? "Venue score" : "Lead score"}</p>
              <p className="text-base font-semibold text-emerald-300">{formatScore(score)}</p>
            </div>
          </section>

          <section className="rounded-xl border border-border/60 bg-background/60 p-3">
            <div className="flex items-center gap-2">
              <LineChart className="h-4 w-4 text-emerald-200" />
              <p className="text-sm font-semibold text-emerald-100">Contact engagement summary</p>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-foreground/80">
              <li>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Last outreach</span>
                <p>{renderSignalSummary(lastContact)}</p>
              </li>
              <li>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Last promo</span>
                <p>{renderSignalSummary(lastPromo)}</p>
              </li>
              <li>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Finance touch</span>
                <p>{renderSignalSummary(lastSettlement)}</p>
              </li>
              <li>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Press reference</span>
                <p>{renderSignalSummary(lastPress)}</p>
              </li>
            </ul>
          </section>

          {insights.suggestions?.length ? (
            <section className="rounded-xl border border-border/60 bg-background/50 p-3">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary-foreground dark:text-amber-200" />
                <p className="text-sm font-semibold text-primary-foreground dark:text-amber-200">Next-step suggestions</p>
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-foreground/80">
                {insights.suggestions.slice(0, 4).map((suggestion, index) => (
                  <li key={`suggestion-${index}`}>{suggestion}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {audienceMap && (
            <section className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="flex items-center gap-2">
                <Map className="h-4 w-4 text-sky-200" />
                <p className="text-sm font-semibold text-sky-100">Music analytics indicators</p>
              </div>
              {typeof audienceMap.summary === "string" && (
                <p className="mt-2 text-sm text-foreground/80">{audienceMap.summary}</p>
              )}
              {Array.isArray(audienceMap.top_markets) && audienceMap.top_markets.length > 0 && (
                <ul className="mt-2 text-xs text-muted-foreground">
                  {audienceMap.top_markets.slice(0, 4).map((market: unknown) => (
                    <li key={String(market)} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
                      {String(market)}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(audienceMap.hotspots) && audienceMap.hotspots.length > 0 && (
                <CodeBlock
                  className="mt-3"
                  value={JSON.stringify(audienceMap.hotspots.slice(0, 3), null, 2)}
                  language="json"
                  collapsible
                />
              )}
            </section>
          )}

          {insights.tools?.manager_context && (
            <section className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="flex items-center gap-2">
                <Newspaper className="h-4 w-4 text-primary-foreground dark:text-amber-200" />
                <p className="text-sm font-semibold text-primary-foreground dark:text-amber-100">Memory summary</p>
              </div>
              <p className="mt-2 text-sm text-foreground/80">
                {typeof insights.tools.manager_context === "string"
                  ? insights.tools.manager_context
                  : JSON.stringify(insights.tools.manager_context, null, 2)}
              </p>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-primary-foreground dark:text-amber-200">Past booking & event history</p>
              <Badge variant="outline">Memory</Badge>
            </div>
            <CRMEpisodesList entityType={objectType} entityId={objectId} limit={6} condensed />
          </section>
        </div>
      ) : null}
      <div className="mt-6 grid gap-2">
        {actionButtons.map((button) => {
          const Icon = button.icon;
          return (
            <Button
              key={button.id}
              variant={button.id === "tools" ? "secondary" : "outline"}
              className="w-full justify-start gap-2 text-sm"
              onClick={() => handleAction(button.id)}
            >
              <Icon className="h-4 w-4" />
              {button.label}
            </Button>
          );
        })}
      </div>
    </aside>
  );
}
